import { z } from "zod";
export const COSMETIC_DEFINITIONS = [
    // Level 1 - streak flair: derived from the effective High-Five streak,
    // auto-applied, lost when the streak breaks.
    {
        id: "streak-ember-ring",
        name: "Ember Ring",
        description: "A warm ember ring for keeping a 7-day High-Five streak alive.",
        level: "streak",
        visual: "ring-ember",
        rarity: "common",
        unlock: { kind: "streakAtLeast", days: 7 },
    },
    {
        id: "streak-fire-ring",
        name: "Fire Ring",
        description: "A blazing animated ring for a 30-day High-Five streak.",
        level: "streak",
        visual: "ring-fire",
        rarity: "rare",
        unlock: { kind: "streakAtLeast", days: 30 },
    },
    {
        id: "streak-legend-flame",
        name: "Legend Flame",
        description: "A legendary flame aura for a 100-day High-Five streak.",
        level: "streak",
        visual: "ring-inferno",
        rarity: "legendary",
        unlock: { kind: "streakAtLeast", days: 100 },
    },
    {
        id: "streak-frost-guard",
        name: "Frost Guard",
        description: "An icy accent shown while a Streak Freeze protects the streak.",
        level: "streak",
        visual: "accent-frost",
        rarity: "uncommon",
        unlock: { kind: "streakFreezeProtected" },
    },
    // Level 2 - badge-tier frames: derived from the global block balance tier,
    // auto-applied, permanent progression.
    {
        id: "frame-normie",
        name: "Clay Frame",
        description: "The starting frame for every member with their first Block.",
        level: "badge",
        visual: "frame-clay",
        rarity: "common",
        unlock: { kind: "badgeTier", tier: "Normie" },
    },
    {
        id: "frame-builder",
        name: "Steel Frame",
        description: "A tempered frame for reaching the Builder tier.",
        level: "badge",
        visual: "frame-steel",
        rarity: "uncommon",
        unlock: { kind: "badgeTier", tier: "Builder" },
    },
    {
        id: "frame-architect",
        name: "Gilded Frame",
        description: "A gilded frame for reaching the Architect tier.",
        level: "badge",
        visual: "frame-gold",
        rarity: "rare",
        unlock: { kind: "badgeTier", tier: "Architect" },
    },
    {
        id: "frame-og",
        name: "Tidal Frame",
        description: "A tidal frame for reaching the OG tier.",
        level: "badge",
        visual: "frame-cyan",
        rarity: "epic",
        unlock: { kind: "badgeTier", tier: "OG" },
    },
    {
        id: "frame-ambassador",
        name: "Royal Frame",
        description: "A royal frame for reaching the Ambassador tier.",
        level: "badge",
        visual: "frame-royal",
        rarity: "legendary",
        unlock: { kind: "badgeTier", tier: "Ambassador" },
    },
    // Level 3 - achievement cosmetics: unlocked by specific achievements and
    // equippable; the member showcases at most one at a time.
    {
        id: "cos-perfectionist-sparkle",
        name: "Flawless Shimmer",
        description: "A sparkle shimmer earned with the Perfectionist achievement.",
        level: "achievement",
        visual: "fx-sparkle",
        rarity: "epic",
        unlock: { kind: "achievement", achievementId: "perfectionist" },
    },
    {
        id: "cos-speedrunner-lightning",
        name: "Lightning Accent",
        description: "A lightning emblem earned with the Speedrunner achievement.",
        level: "achievement",
        visual: "fx-lightning",
        rarity: "rare",
        unlock: { kind: "achievement", achievementId: "speedrunner" },
    },
    {
        id: "cos-unbroken-aura",
        name: "Unbroken Aura",
        description: "A legendary aura earned with the Unbroken achievement.",
        level: "achievement",
        visual: "fx-aura",
        rarity: "legendary",
        unlock: { kind: "achievement", achievementId: "year-long-streak" },
    },
    {
        id: "cos-early-adopter-founder",
        name: "Founder's Ring",
        description: "A founder's ring earned with the Early Adopter achievement.",
        level: "achievement",
        visual: "fx-founder-ring",
        rarity: "uncommon",
        unlock: { kind: "achievement", achievementId: "early-adopter" },
    },
    {
        id: "cos-streak-master-flame",
        name: "Flame Emblem",
        description: "A flame emblem earned with the Streak Master achievement.",
        level: "achievement",
        visual: "fx-flame-emblem",
        rarity: "rare",
        unlock: { kind: "achievement", achievementId: "streak-master" },
    },
];
const cosmeticsById = new Map(COSMETIC_DEFINITIONS.map((cosmetic) => [cosmetic.id, cosmetic]));
const cosmeticsByAchievementId = new Map(COSMETIC_DEFINITIONS.filter((cosmetic) => cosmetic.unlock.kind === "achievement").map((cosmetic) => [
    cosmetic.unlock.achievementId,
    cosmetic,
]));
const cosmeticsByBadgeTier = new Map(COSMETIC_DEFINITIONS.filter((cosmetic) => cosmetic.unlock.kind === "badgeTier").map((cosmetic) => [
    cosmetic.unlock.tier,
    cosmetic,
]));
/**
 * Streak flair thresholds, highest first, so the first matching tier is the
 * one to render. Must stay in sync with the `streakAtLeast` entries above.
 */
export const STREAK_FLAIR_TIERS = [
    { minStreak: 100, cosmeticId: "streak-legend-flame" },
    { minStreak: 30, cosmeticId: "streak-fire-ring" },
    { minStreak: 7, cosmeticId: "streak-ember-ring" },
];
export function getCosmeticById(id) {
    return cosmeticsById.get(id) ?? null;
}
export function getStreakFlairCosmetic(effectiveStreak) {
    for (const tier of STREAK_FLAIR_TIERS) {
        if (effectiveStreak >= tier.minStreak) {
            return getCosmeticById(tier.cosmeticId);
        }
    }
    return null;
}
export function getFrameCosmeticForBadgeTier(tier) {
    if (!tier)
        return null;
    return cosmeticsByBadgeTier.get(tier) ?? null;
}
export function getCosmeticForAchievement(achievementId) {
    return cosmeticsByAchievementId.get(achievementId) ?? null;
}
/**
 * Only achievement-level cosmetics are equippable; streak and badge flair are
 * derived and auto-applied, so they can never be pinned as the showcase.
 */
export function isEquippableCosmeticId(id) {
    return getCosmeticById(id)?.level === "achievement";
}
export const EquipCosmeticBodySchema = z.object({
    cosmeticId: z.string().min(1).nullable(),
});
